import immutableStore from 'alt-utils/lib/ImmutableUtil';
import immutable, {Map} from 'immutable';

import alt from 'components/Dispatcher';
import {createSavedSearchFromServerObject} from 'records/SavedSearch';
import SearchStore from 'stores/SearchStore';
import CallActions from 'actions/CallActions';
import SearchFieldsStore from 'stores/search/SearchFieldsStore';
import ComponentUtils from 'components/ComponentUtils';
import SavedSearchActions from 'actions/search/SavedSearchActions';
import SavedSearchSource from 'sources/SavedSearchSource';
import AuthStore from 'stores/AuthStore';

/**
 * Stores saved searches from related endpoints including /api/v1/saved_searches
 * and /api/v1/saved_search/{id}
 */
@immutableStore
class SavedSearchStore {
  constructor() {
    this.state = immutable.Map({
      // activeSearches are searches that are saved while a user is "in" the search mode. Every time the
      // clears a search term, all the activeSearches get merged into this.state.get('searches')
      activeSearches: immutable.List([]),
      openActionPopover: false,
      searches: immutable.List([]),
      searchesLoading: false,
    });
    this.bindActions(SavedSearchActions);
    this.registerAsync(SavedSearchSource);
    this.utils = new ComponentUtils();
  }

  /**
   * Return a data object that can be passed to the backend. A data object stores
   * search values so that they can be returned as part of a saved search later to
   * re-cosntruct the same search query
   * @param {object} state SearchStore.getState()
   * @return {object} An object representing the current search
   */
  searchData(state) {
    const fields = this.searchFieldsData(state);
    return {
      term: state.get('searchTerm'),
      fields: fields,
      limit: state.get('searchLimit'),
      page: state.get('searchPage'),
    };
  }

  searchFieldsData(state) {
    let fields = state.get('searchFields');
    if (state.get('isSearchingSelf')) {
      fields = fieldsAugmentedWithSelf(fields);
    }
    if (state.get('fileId')) {
      fields = fieldsAugmentedWithFileId(fields);
    }
    return fields;
  }

  /**
   * Return a state object that can be passed to the backend. A state object stores the
   * states of search widgets so that they can be returned as part of saved search later
   * to re-render the search sidebar
   * @param  {object} state SearchFieldsStore.getState()
   * @return {object}       An object representing the states of the search widgets
   */
  searchState(state) {
    return state.get('searchFieldsState').toObject();
  }

  /**
   * Delete a saved search so that it's removed from the front-end rendering
   * @param  {object} savedSearch A saved search object
   */
  onDeleteSavedSearch(savedSearch) {
    setImmediate(() => {
      this.getInstance().delete(savedSearch.id);
    });
  }

  /**
   * Removed a saved search from this.state.get('searches') and this.state.get('activeSearches')
   * so that it won't be rendered on the ui anymore
   * @param  {string} id Id of a savedSearch record
   */
  onDeleteSuccess(args) {
    const id = args[0];
    const searches = this.state.get('searches').filterNot(search => search.id === id);
    const activeSearches = this.state.get('activeSearches').filterNot(search => search.id === id);
    this.setState(
      this.state.merge(
        immutable.Map({
          activeSearches: activeSearches,
          searches: searches,
        })
      )
    );
  }

  /**
   * Since a user clicked an saved search while in an inactive state, we use `savedSearch` to perforrm
   * an expression / fields based search along with its term and update the search results and sidebar
   * @param  {immutable/Record} savedSearch A SavedSearch Record
   */
  onExecuteInactiveSearch(savedSearch) {
    this.executeSavedSearch(savedSearch);
  }

  /**
   * Retrieve a saved search and then execute it
   * @param  {string} savedSearchId A saved search id
   */
  onRetrieveAndExecuteSavedSearch(savedSearchId) {
    this.getInstance().get(savedSearchId);
  }

  /**
   * Retrived a saved search and now we should execute it
   * @param  {object} response A successful `GET /api/v1/saved_search/{id}` response
   */
  onGetSuccess(response) {
    this.executeSavedSearch(createSavedSearchFromServerObject(response.data.saved_search));
  }

  /**
   * Execute a saved search to make it become the active search
   * @param  {object} savedSearch A SavedSearch object
   */
  executeSavedSearch(savedSearch) {
    setTimeout(() => {
      CallActions.executeInactiveSearch(savedSearch);
    }, 0);
  }

  /**
   * Persist the current search data in SearchStore onto the backend
   */
  onPersistCurrentSearch(title) {
    this.setState(this.state.set('openActionPopover', true));
    const searchData = this.searchData(SearchStore.getState());
    const searchState = this.searchState(SearchFieldsStore.getState());
    setImmediate(() => {
      this.getInstance().persist(title, searchData, searchState);
    });
    this.preventDefault();
  }

  /** Append resp.data.saved_search to this.state.get('activeSearches') so that it can be rendered */
  onPersistSuccess(resp) {
    let activeSearches = this.state.get('activeSearches');
    const savedSearch = resp.data.saved_search;
    activeSearches = activeSearches.push(createSavedSearchFromServerObject(savedSearch));
    let state = this.state.set('activeSearches', activeSearches);
    state = state.set('openActionPopover', false);
    this.setState(state);
  }

  /**
   * Save fetched saved searches returned from the server in this.state.get('searches')
   * @param  {object} resp A server-side response object whose data containts the saved_searches
   */
  onFetchSuccess(resp) {
    const savedSearches = immutable
      .List(resp.data.saved_searches)
      .map(x => createSavedSearchFromServerObject(x));
    const state = this.state.set('searches', savedSearches);
    this.setState(state);
  }

  /**
   * Set this.state.searchesLoading to true so that we can show a loading animation / icon to the user
   */
  onFetchLoading() {
    const state = this.state.set('searchesLoading', true);
    this.setState(state);
  }

  /**
   * Server returned a 40x response
   * @param  {object} resp A server-side 40x response
   */
  onFetchError(resp) {
    console.log('error');
  }

  /**
   * Merge this.state.get('activeSearches') into this.state.get('searches')
   */
  onMergeActiveIntoSearches() {
    let searches = this.state.get('searches');
    let activeSearches = this.state.get('activeSearches');
    searches = searches.concat(activeSearches);
    let state = this.state.set('searches', searches);
    state = state.set('activeSearches', immutable.List([]));
    this.setState(state);
  }
}

export default alt.createStore(SavedSearchStore, 'SavedSearchStore');

function fieldsAugmentedWithSelf(fields) {
  const user = AuthStore.getState().user;
  if (Map.isMap(fields)) {
    fields = fields.toObject();
  }
  fields['user_id'] = user.id;
  return fields;
}

function fieldsAugmentedWithFileId(fileId, fields) {
  if (Map.isMap(fields)) {
    fields = fields.toObject();
  }
  fields.Id = fileId;
  return fields;
}
